import type { Dispatch, ReactNode, SetStateAction } from "react";
import { createContext, useContext, useState, useEffect, useCallback } from "react";
import apiService from "@/services/apiService";
import clinicalSettingsService from "@/services/clinicalSettingsService";
import { disableEmergencyPush } from '@/services/emergencyPush';

export interface AuthUser {
  id: number;
  email?: string;
  username?: string;
  firstname?: string;
  lastname?: string;
  hospital_name?: string;
  tenant_id?: number;
  is_super_admin?: boolean;
  roles?: string[];
  role?: string;
  role_name?: string;
  full_name?: string;
  name?: string;
}
interface LoginCredentials { email: string; password: string }
type AuthResult = { success: true; challenge?: any; session?: any } | { success: false; error: string };
type RefreshResult = { success: true; token: string; refreshToken: string } | { success: false; error: string };
interface AuthContextValue {
  user: AuthUser | null;
  token: string | null;
  refreshToken: string | null;
  loading: boolean;
  error: string | null;
  permissions: Set<string>;
  isSuperAdmin: boolean;
  permissionsLoaded: boolean;
  hasPermission: (code: string) => boolean;
  login: (credentials: LoginCredentials) => Promise<AuthResult>;
  verifyMfaLogin: (challenge: string, code: string, enrollment?: boolean) => Promise<AuthResult>;
  completeMfaEnrollmentLogin: (loginData: any) => Promise<AuthResult>;
  logout: () => void;
  clearSession: () => void;
  updateUserProfile: (updates: Partial<AuthUser>) => void;
  isAuthenticated: () => boolean;
  setError: Dispatch<SetStateAction<string | null>>;
  refreshAccessToken: (refreshTokenValue: string) => Promise<RefreshResult>;
}
const AuthContext = createContext<AuthContextValue | null>(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [refreshToken, setRefreshToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [permissions, setPermissions] = useState<Set<string>>(new Set());
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  // Lets the nav tell "not loaded yet" from "no permission".
  const [permissionsLoaded, setPermissionsLoaded] = useState(false);

  // Best-effort: a user with no resolvable permissions just sees fewer
  // gated actions, same as the backend failing closed on the same call.
  const loadPermissions = async () => {
    try {
      const res = await apiService.get("/rbac/me/permissions");
      setIsSuperAdmin(!!res?.isSuperAdmin);
      setPermissions(new Set(res?.permissions || []));
    } catch (err) {
      console.error("Could not load permissions:", err);
      setIsSuperAdmin(false);
      setPermissions(new Set());
    } finally {
      setPermissionsLoaded(true);
    }
  };

  const hasPermission = (code: string) => isSuperAdmin || permissions.has(code);

  // Check for existing token on mount
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const storedToken = localStorage.getItem("auth_token");
        const storedRefreshToken = localStorage.getItem("auth_refresh_token");
        const storedUser = localStorage.getItem("auth_user");

        if (storedToken && storedRefreshToken && storedUser) {
          const parsedUser = JSON.parse(storedUser);
          if (!parsedUser.hospital_name || parsedUser.hospital_name.trim() === "Hospital") {
            parsedUser.hospital_name =
              clinicalSettingsService.getHospitalPreferences().hospitalName ||
              "DaAFYA Medical Center";
            localStorage.setItem("auth_user", JSON.stringify(parsedUser));
          }

          // Check if token is expired
          const tokenPayload = JSON.parse(atob(storedToken.split(".")[1]));
          const isExpired = Date.now() >= tokenPayload.exp * 1000;

          if (isExpired) {
            console.log("🔄 Token expired, attempting refresh...");
            const refreshResult = await refreshAccessToken(storedRefreshToken);
            if (refreshResult.success) {
              setToken(refreshResult.token);
              setRefreshToken(refreshResult.refreshToken);
              setUser(parsedUser);
            } else {
              // Refresh failed, clear everything
              localStorage.removeItem("auth_token");
              localStorage.removeItem("auth_refresh_token");
              localStorage.removeItem("auth_user");
            }
          } else {
            setToken(storedToken);
            setRefreshToken(storedRefreshToken);
            setUser(JSON.parse(storedUser));
          }
          await loadPermissions();
        }
      } catch (err) {
        console.error("Error initializing auth:", err);
        // Clear invalid stored data
        localStorage.removeItem("auth_token");
        localStorage.removeItem("auth_refresh_token");
        localStorage.removeItem("auth_user");
      } finally {
        setLoading(false);
      }
    };

    initializeAuth();
  }, []);

  const getApiBaseUrl = () => {
    const envBase = (import.meta as any).env?.VITE_API_BASE_URL;
    return envBase && envBase !== "undefined" ? envBase.replace(/\/+$/, "") : "";
  };

  const refreshAccessToken = async (refreshTokenValue: string): Promise<RefreshResult> => {
    try {
      console.log("🔄 Refreshing access token...");
      const apiBase = getApiBaseUrl();
      const response = await fetch(
        `${apiBase}/auth/refresh`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ refreshToken: refreshTokenValue }),
        }
      );

      if (!response.ok) {
        throw new Error("Token refresh failed");
      }

      const data = await response.json();
      console.log("✅ Token refreshed successfully");

      // Update localStorage
      localStorage.setItem("auth_token", data.access_token);
      localStorage.setItem("auth_refresh_token", data.refresh_token);

      return {
        success: true,
        token: data.access_token,
        refreshToken: data.refresh_token,
      };
    } catch (err) {
      console.error("❌ Token refresh failed:", err);
      return { success: false, error: (err instanceof Error ? err.message : "Authentication failed") };
    }
  };

  const login = async (credentials: LoginCredentials): Promise<AuthResult> => {
    try {
      setLoading(true);
      setError(null);


      // Real API login
      const apiBase = getApiBaseUrl();
      const response = await fetch(
        `${apiBase}/auth/login`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(credentials),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Login failed");
      }

      const loginData = await response.json();

      if (loginData.mfa_required) return { success: true, challenge: loginData };
      return await establishSession(loginData);
    } catch (err) {
      console.error("❌ Login error:", err);
      setError((err instanceof Error ? err.message : "Authentication failed"));
      return { success: false, error: (err instanceof Error ? err.message : "Authentication failed") };
    } finally {
      setLoading(false);
    }
  };

  const establishSession = async (loginData: any): Promise<AuthResult> => {
      const authToken = loginData.access_token;
      const refreshTokenValue = loginData.refresh_token;
      const defaultHospitalName =
        clinicalSettingsService.getHospitalPreferences().hospitalName ||
        "DaAFYA Medical Center";

      const userData = {
        id: loginData.user.id,
        email: loginData.user.email,
        username: loginData.user.username,
        firstname: loginData.user.firstname,
        lastname: loginData.user.lastname,
        is_super_admin: loginData.user.is_super_admin,
        // The hospital this login is for (the API returns it as `tenant`).
        // Name and id only -- never the database details.
        tenant_id: loginData.tenant?.id,
        hospital_name: loginData.tenant?.name || defaultHospitalName,
        roles: loginData.roles,
      };

      // Store in localStorage
      localStorage.setItem("auth_token", authToken);
      localStorage.setItem("auth_refresh_token", refreshTokenValue);
      localStorage.setItem("auth_user", JSON.stringify(userData));

      // Update state
      setToken(authToken);
      setRefreshToken(refreshTokenValue);
      setUser(userData);
      await loadPermissions();

      return { success: true, session: { mfa_policy: loginData.mfa_policy, recovery_codes: loginData.recovery_codes } };
  };

  const verifyMfaLogin = async (challenge: string, code: string, enrollment = false): Promise<AuthResult> => {
    try {
      setLoading(true); setError(null);
      const apiBase = getApiBaseUrl();
      const response = await fetch(`${apiBase}/auth/mfa/${enrollment ? "enroll/verify" : "verify"}`, {
        method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ challenge, code }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || "Could not verify the code");
      if (enrollment) return { success: true, session: { pendingLoginData: data, recovery_codes: data.recovery_codes || [] } };
      return await establishSession(data);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Authentication failed";
      setError(message); return { success: false, error: message };
    } finally { setLoading(false); }
  };

  const completeMfaEnrollmentLogin = async (loginData: any): Promise<AuthResult> => {
    try {
      setLoading(true);
      setError(null);
      return await establishSession(loginData);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Could not finish signing in";
      setError(message);
      return { success: false, error: message };
    } finally {
      setLoading(false);
    }
  };

  /**
   * Revokes this user's refresh tokens on the server, so a copied token stops
   * working after sign-out. If the access token has already expired, it is
   * renewed once first. Best-effort: offline, the tokens still expire.
   */
  const revokeOnServer = async (accessToken: string | null, refreshTokenValue: string | null) => {
    const apiBase = getApiBaseUrl();
    const postLogout = (bearer: string) =>
      fetch(`${apiBase}/auth/logout`, { method: "POST", headers: { Authorization: `Bearer ${bearer}` } });
    try {
      const res = accessToken ? await postLogout(accessToken) : null;
      if ((!res || res.status === 401) && refreshTokenValue) {
        const renewed = await fetch(`${apiBase}/auth/refresh`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ refreshToken: refreshTokenValue }),
        });
        const data = renewed.ok ? await renewed.json() : null;
        if (data?.access_token) await postLogout(data.access_token);
      }
    } catch (err) {
      console.warn("Sign-out could not reach the server; tokens will expire on their own", err);
    }
  };

  const clearSession = useCallback(() => {
    localStorage.removeItem("auth_token");
    localStorage.removeItem("auth_refresh_token");
    localStorage.removeItem("auth_user");
    apiService.clearToken();
    setToken(null);
    setRefreshToken(null);
    setUser(null);
    setError(null);
    setPermissions(new Set());
    setIsSuperAdmin(false);
    setPermissionsLoaded(false);
  }, []);

  const updateUserProfile = useCallback((updates: Partial<AuthUser>) => {
    setUser((currentUser) => {
      if (!currentUser) return currentUser;
      const nextUser = { ...currentUser, ...updates };
      try {
        localStorage.setItem("auth_user", JSON.stringify(nextUser));
      } catch {
        // The in-memory profile remains updated when browser storage is unavailable.
      }
      return nextUser;
    });
  }, []);

  const logout = () => {
    // Read before the tokens are cleared below; the server call runs in the background.
    void revokeOnServer(localStorage.getItem("auth_token"), localStorage.getItem("auth_refresh_token"));
    void disableEmergencyPush().catch(console.warn);
    clearSession();
  };

  const isAuthenticated = () => {
    return !!(token && user);
  };

  const value = {
    user,
    token,
    refreshToken,
    loading,
    error,
    login,
    verifyMfaLogin,
    completeMfaEnrollmentLogin,
    logout,
    clearSession,
    updateUserProfile,
    isAuthenticated,
    setError,
    refreshAccessToken,
    permissions,
    isSuperAdmin,
    hasPermission,
    permissionsLoaded,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
